import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { calculateContainerAlgoPoints } from "@CommonUtils/pog/container";
import { SpaceMixLabel } from "../panels/SpaceMix/SpaceMix";

export const calculateSpaceMix = (pog: Pog, isPog: boolean, mode: SpaceMixLabel) => {
  const spaceMix: {
    individual: {
      [key: string]: {
        cdt0: string;
        cdt1: string;
        dimensions: number;
        sales: number;
        salesAmount: number;
        cdt1Priority: number;
        isCdt1NonUnique?: boolean;
      };
    };
    total: {
      area: number;
      dimensionsSum: number;
      salesSum: number;
      salesAmountSum: number;
    };
  } = {
    individual: {},
    total: {
      area: pog.planogram.getTotalSpace(),
      dimensionsSum: 0,
      salesSum: 0,
      salesAmountSum: 0,
    },
  };
  if (isPog) {
    const cdt1Map: Record<string, boolean> = {};
    const generateKey = (item: { variant: string; cdt1: string }) => {
      return mode === "CDT0" ? item.variant : `${item.variant}-${item.cdt1}`;
    };
    // buffer to count non-unique cdt1s
    const cdts: Map<string, Set<string>> = new Map<string, Set<string>>();
    const salesAmountSeenProductCodes = new Set<string>();
    for (const bay of pog.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          const itemWidth = item.getWidth();
          const dimensionWidth = (itemWidth * item.facings) / (item.facingsRows || 1);
          const key = generateKey(item);
          if (!spaceMix.individual[key]) {
            spaceMix.individual[key] = {
              cdt0: item.variant,
              cdt1: item.cdt1,
              dimensions: dimensionWidth,
              sales: item.spaceMix,
              salesAmount: item.salesAmount,
              cdt1Priority: item.cdt1Priority,
            };
            if (!cdts.has(item.cdt1)) {
              cdts.set(item.cdt1, new Set<string>());
            }
            cdts.get(item.cdt1)?.add(item.variant);
            spaceMix.total.salesSum += item.spaceMix;
            if (!salesAmountSeenProductCodes.has(item.productCode)) {
              spaceMix.total.salesAmountSum += item.salesAmount;
              salesAmountSeenProductCodes.add(item.productCode);
            }
            // only sum up sales for variant mode
            // in cdt1 mode and we're facing same variant, same cdt1 we dont want to double count sales
          } else if (!cdt1Map[`${item.variant}-${item.cdt1}`] && mode === "CDT0") {
            spaceMix.total.salesSum += item.spaceMix;
            spaceMix.individual[key].sales += item.spaceMix;
            spaceMix.individual[key].dimensions += dimensionWidth;
            if (!salesAmountSeenProductCodes.has(item.productCode)) {
              spaceMix.total.salesAmountSum += item.salesAmount;
              spaceMix.individual[key].salesAmount += item.salesAmount;
              salesAmountSeenProductCodes.add(item.productCode);
            }
          } else {
            spaceMix.individual[key].dimensions += dimensionWidth;
            if (!salesAmountSeenProductCodes.has(item.productCode)) {
              spaceMix.total.salesAmountSum += item.salesAmount;
              spaceMix.individual[key].salesAmount += item.salesAmount;
              salesAmountSeenProductCodes.add(item.productCode);
            }
          }
          cdt1Map[`${item.variant}-${item.cdt1}`] = true;
          spaceMix.total.dimensionsSum += dimensionWidth;
        }
      }
    }
    for (const item of pog.unrangedItems) {
      const key = generateKey(item);
      if (!spaceMix.individual[key]) {
        spaceMix.individual[key] = {
          cdt0: item.variant,
          cdt1: item.cdt1,
          dimensions: 0,
          sales: item.spaceMix,
          salesAmount: 0,
          cdt1Priority: item.cdt1Priority,
        };
        spaceMix.total.salesSum += item.spaceMix;
      }
    }
    if (mode === "CDT1") {
      cdts.forEach((variants, cdt1) => {
        if (variants.size > 1) {
          variants.forEach((variant) => {
            const key = `${variant}-${cdt1}`;
            spaceMix.individual[key].isCdt1NonUnique = true;
          });
        }
      });
    }
  }

  // unrangedContainer should have 0 currentWidth, so we need to filter them out.
  const unrangedContainerIds = new Set(
    pog.unrangedContainers.map((unrangedContainer) => unrangedContainer.uniqueId),
  );
  for (const container of [...pog.containers, ...pog.unrangedContainers]) {
    const label = container.getLabel();
    const containerShelvesWidthUsed = unrangedContainerIds.has(container.uniqueId)
      ? 0
      : calculateContainerAlgoPoints(container, pog).currentTargetWidth;

    if (!spaceMix.individual[label]) {
      spaceMix.individual[label] = {
        cdt0: container.cdts[0].cdt0,
        cdt1: "cdt1" in container.cdts[0] ? container.cdts[0].cdt1 : "",
        dimensions: containerShelvesWidthUsed,
        sales: container.sales,
        salesAmount: 0,
        cdt1Priority: 0, // Hardcoded as 0, because containers dont have cdt priority information.
      };
      // Container sales is duplicated between containers, because there can be split containers.
      // We only count container sales once for the total sales.
      spaceMix.total.salesSum += container.sales;
    } else {
      spaceMix.individual[label].dimensions += containerShelvesWidthUsed;
    }
    spaceMix.total.dimensionsSum += containerShelvesWidthUsed;
  }

  return spaceMix;
};
